import { Keychain } from '@velorum/browser-authenticator';

import { Injectable, inject } from '@angular/core';
import { combineLatest, forkJoin, from, Observable, of } from 'rxjs';
import { defaultIfEmpty, map, mergeMap, switchMap } from 'rxjs/operators';

import { AuthenticationRequest, Policy } from '@ap/fido-uaf-frontend-client';
import { AuthenticationToken, RegistrationStage, ApAuthenticationService } from '@ap/browser-authenticator';
import { FidoUafRegistrationResponse, TokenTypeId, ApAuthenticatieFidoResource } from '@ap/interface-apic-authenticatie-fido';

import { ValidationResult } from '../../validation/validation.model';
import { ApTokenService } from '../token.service';

@Injectable({ providedIn: 'root' })
export class ApBrowserService {
    private readonly authenticationFidoResource = inject(ApAuthenticatieFidoResource);
    private readonly tokenService = inject(ApTokenService);
    private readonly authenticationService = inject(ApAuthenticationService);

    public static filterPendingTokens(allowPendingTokens: boolean, authenticationService: ApAuthenticationService) {
        return (source: Observable<string[]>): Observable<string[]> =>
            allowPendingTokens
                ? source
                : source.pipe(
                      mergeMap((ids) => (ids.length === 0 ? of([]) : forkJoin(ids.map((id) => authenticationService.getToken(id))))),
                      map((tokens) => tokens.filter((token) => token.stage === RegistrationStage.VERIFIED).map((token) => token.keychainId))
                  );
    }

    public getTokensForPolicy(policy: Policy, allowPendingTokens: boolean): Observable<AuthenticationToken[]> {
        if (this.tokenService.isAnonymous(policy)) {
            return this.getTokens(allowPendingTokens);
        }
        const browserIds = this.tokenService.getKeyIds(policy, TokenTypeId.BROWSER_DEVICE);
        const pinIds = this.tokenService.getKeyIds(policy, TokenTypeId.BROWSER_PINCODE);
        const acceptedKeychainIds = this.authenticationService.getMatchingKeychainIds(browserIds, pinIds);
        return combineLatest([this.getTokens(allowPendingTokens), acceptedKeychainIds]).pipe(
            map(([allTokens, acceptedIds]: [AuthenticationToken[], string[]]) =>
                allTokens.filter((token) => acceptedIds.indexOf(token.keychainId) > -1)
            )
        );
    }

    public getTokens(allowPendingTokens: boolean): Observable<AuthenticationToken[]> {
        return this.authenticationService.getTokenIds().pipe(
            ApBrowserService.filterPendingTokens(allowPendingTokens, this.authenticationService),
            switchMap((tokenIds: string[]) => forkJoin(tokenIds.map((tokenId: string) => this.authenticationService.getToken(tokenId)))),
            defaultIfEmpty([])
        );
    }

    public deleteToken(keychainId: string): Observable<void> {
        return this.authenticationService.deleteToken(keychainId);
    }

    public registerToken(registrationId: string, browserAlias: string, pincode: string, username: string): Observable<string> {
        const keychain = this.authenticationService.generateToken(browserAlias, username);
        return this.authenticationFidoResource.getOneRegistrationRequest(registrationId).pipe(
            switchMap((registrationRequest) =>
                this.authenticationService.generateRegistrationResponse(keychain, registrationRequest, pincode)
            ),
            switchMap((registrationResponse) => this.finalizeRegistration(registrationId, registrationResponse, keychain)),
            map(() => keychain.getId())
        );
    }

    public validateToken(
        authenticationRequest: AuthenticationRequest,
        keychainId: string,
        pincode: string,
        actionId: string
    ): Observable<ValidationResult> {
        return from(this.authenticationService.generateAuthenticationResponse(keychainId, authenticationRequest, pincode)).pipe(
            switchMap((response) =>
                this.tokenService.validateResponse(response, this.tokenService.isAnonymous(authenticationRequest.policy), actionId)
            )
        );
    }

    public getBrowserBindingKey(keychainId: string): Observable<string> {
        return this.authenticationService.getBrowserBindingKey(keychainId);
    }

    private finalizeRegistration(
        registrationId: string,
        registrationResponse: FidoUafRegistrationResponse,
        keychain: Keychain
    ): Observable<void> {
        return of(this.authenticationService.storeToken(keychain)).pipe(
            switchMap(() => this.authenticationFidoResource.putOneRegistrationResponse(registrationId, registrationResponse))
        );
    }
}
